import {
    Avatar, Box, Divider, IconButton, ListItemIcon,
    Menu, MenuItem, Tooltip, Typography
} from "@mui/material";
import SyncIcon from '@mui/icons-material/Sync';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ExtensionIcon from '@mui/icons-material/Extension';
import LogoutIcon from '@mui/icons-material/Logout';
import React, { useState } from "react";
import { getBaseURLWithAuth, getSessionStorage } from "../common/utils/session";
import { sessionKeys } from "../common/utils/session/sessionKeys";
import { IUserTokenDataDto } from "../interfaces/user/IUserTokenDataDto";
import Loader from "../components/loader";
import { tryLogout } from "../services/logout/logout.service";
import { useNavigate } from "react-router-dom";
import { useNotification } from "../components/notification/useNotification";
import extractErrorMessage from "../common/utils/errorMessages";
import useLazyLoading from "../common/hooks/loader";
import { tryToUpdateCryptoCurrencyPrices } from "../services/crypto-currency/cripto-currency.service";

export default function UserIcon() {

    const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(null);
    const [loading, setLoading] = useState(false);
    const showLoading = useLazyLoading(loading);
    const navigate = useNavigate();
    const { displayNotification } = useNotification();

    const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorElUser(event.currentTarget);
    };

    const handleCloseUserMenu = () => {
        setAnchorElUser(null);
    };

    const updatePrices = async () => {
        handleCloseUserMenu();
        setLoading(true);

        try {
            var updatePricesResponse = await tryToUpdateCryptoCurrencyPrices();

            if (updatePricesResponse.status === 200) {
                displayNotification({ type: 'success', message: 'Preços actualizados com sucesso.' });
            }

        } catch (error) {
            displayNotification({ type: 'error', message: extractErrorMessage(error) });
        }
        finally {
            setLoading(true);
            window.location.reload();
        }
    }

    const executeLogout = async () => {
        handleCloseUserMenu();
        setLoading(true);

        try {
            var response = await tryLogout();

            if (response?.status === 200) {
                sessionStorage.clear();
                setLoading(false);
                navigate('/');
            } else {
                displayNotification({ type: 'error', message: 'Ocorreu um erro a efectuar o logout.' });
            }

        } catch (error: any) {
            setLoading(false);
            displayNotification({ type: 'error', message: extractErrorMessage(error) ?? 'Ocorreu um erro a efectuar o logout.' });
        }
    }

    const getUser = (): IUserTokenDataDto | null => {
        const userJson = getSessionStorage(sessionKeys.user);
        return userJson ? JSON.parse(userJson) : null;
    }

    const getInitials = () => {
        const user = getUser();
        if (!user) return '';
        const parts = user.UserName.split(' ');
        return parts.length >= 2
            ? `${parts[0][0]}${parts[1][0]}`
            : parts[0][0];
    }

    const getFullName = () => {
        return getUser()?.UserName ?? '';
    }

    return (
        <>
            {showLoading === true ? <Loader /> : null}
            <Box sx={{ flexGrow: 0 }}>
                <Tooltip title="Definições">
                    <IconButton
                        onClick={handleOpenUserMenu}
                        sx={{
                            p: 0.5,
                            border: '2px solid',
                            borderColor: 'primary.main',
                            borderRadius: '50%',
                            '&:hover': { borderColor: 'primary.light', bgcolor: 'rgba(255,215,0,0.08)' },
                        }}
                    >
                        <Avatar
                            sx={{
                                bgcolor: 'primary.main',
                                color: '#000',
                                fontWeight: 700,
                                fontSize: '0.85rem',
                                width: 32,
                                height: 32,
                            }}
                        >
                            {getInitials()}
                        </Avatar>
                    </IconButton>
                </Tooltip>

                <Menu
                    anchorEl={anchorElUser}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    open={Boolean(anchorElUser)}
                    onClose={handleCloseUserMenu}
                    disableScrollLock
                    slotProps={{
                        paper: {
                            sx: {
                                mt: 1,
                                minWidth: 200,
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: 'divider',
                                boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                            },
                        },
                    }}
                >
                    {/* User info header */}
                    <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar
                            sx={{
                                bgcolor: 'primary.main',
                                color: '#000',
                                fontWeight: 700,
                                fontSize: '0.85rem',
                                width: 36,
                                height: 36,
                            }}
                        >
                            {getInitials()}
                        </Avatar>
                        <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{ color: 'text.primary', lineHeight: 1.3 }}
                        >
                            {getFullName()}
                        </Typography>
                    </Box>

                    <Divider sx={{ borderColor: 'divider', mb: 0.5 }} />

                    <MenuItem onClick={updatePrices} sx={{ py: 1, px: 2, gap: 1.5, borderRadius: 1, mx: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 'unset', color: 'text.secondary' }}>
                            <SyncIcon fontSize="small" />
                        </ListItemIcon>
                        <Typography variant="body2">Actualizar Preços</Typography>
                    </MenuItem>

                    <MenuItem
                        onClick={() => { handleCloseUserMenu(); navigate(getBaseURLWithAuth() + 'edit'); }}
                        sx={{ py: 1, px: 2, gap: 1.5, borderRadius: 1, mx: 0.5 }}
                    >
                        <ListItemIcon sx={{ minWidth: 'unset', color: 'text.secondary' }}>
                            <PersonOutlineIcon fontSize="small" />
                        </ListItemIcon>
                        <Typography variant="body2">Utilizador</Typography>
                    </MenuItem>

                    <MenuItem
                        onClick={() => { handleCloseUserMenu(); navigate(getBaseURLWithAuth() + 'integrations'); }}
                        sx={{ py: 1, px: 2, gap: 1.5, borderRadius: 1, mx: 0.5 }}
                    >
                        <ListItemIcon sx={{ minWidth: 'unset', color: 'text.secondary' }}>
                            <ExtensionIcon fontSize="small" />
                        </ListItemIcon>
                        <Typography variant="body2">Integrações</Typography>
                    </MenuItem>

                    <Divider sx={{ borderColor: 'divider', mt: 0.5 }} />

                    <MenuItem
                        onClick={executeLogout}
                        sx={{
                            py: 1, px: 2, gap: 1.5, borderRadius: 1, mx: 0.5, mt: 0.5,
                            color: 'error.main',
                            '&:hover': { bgcolor: 'rgba(211,47,47,0.08)' },
                        }}
                    >
                        <ListItemIcon sx={{ minWidth: 'unset', color: 'error.main' }}>
                            <LogoutIcon fontSize="small" />
                        </ListItemIcon>
                        <Typography variant="body2" color="inherit">Logout</Typography>
                    </MenuItem>
                </Menu>
            </Box>
        </>
    )
}
